import { useState, useCallback, useEffect } from "react";
import { useParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getAttendanceScheduleDetail } from "api/attendance";
import { getPracticeScheduleDetail } from "api/class";
import api from "utils/api";
import SubHeader from "components/common/SubHeader";
import dayjs from "dayjs";
import { useSnackbar } from "contexts/SnackbarContext";
import PbInputSheet from "components/profile/PbInputSheet";
import { getPublicUserKey } from "utils/userIdentity";
import { startDirectMessageRoom } from "api/chat";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faCalendar, 
    faClock, 
    faEdit,
    faCheckCircle,
    faMessage
} from "@fortawesome/free-regular-svg-icons";
import { 
    faMapMarkerAlt, 
    faPhone, 
} from "@fortawesome/free-solid-svg-icons";

const ATTENDANCE_STATUS = {
    PENDING: "pending",
    ATTENDED: "attended",
    ABSENT: "absent",
    NEEDS_RETAKE: "needs_retake",
};

const AdminAttendanceDetail = () => {
    const navigate = useNavigate();
    const classId = useParams().id;
    const [searchParams] = useSearchParams();
    const { type: stateType } = useLocation().state || {};
    // 새 창(window.open) 진입은 state가 없으므로 쿼리스트링을 우선 사용한다.
    const type = searchParams.get("type") || stateType || "session";

    const { showSuccess, showError } = useSnackbar();
    const [scheduleInfo, setScheduleInfo] = useState(null);
    const [sessionAttendanceList, setSessionAttendanceList] = useState([]);
    const [practiceAttendanceList, setPracticeAttendanceList] = useState([]);
    const [practiceId, setPracticeId] = useState(null);
    const [practiceDetail, setPracticeDetail] = useState(null);
    const [isSessionActive, setIsSessionActive] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isCompleting, setIsCompleting] = useState(false);
    const [pbTarget, setPbTarget] = useState(null);

    useEffect(() => {
        getAttendanceScheduleDetail(classId, type).then((res) => {
            if (res) {
                const activeSession = res.session && res.session.status !== "CANCELLED" ? res.session : null;
                setIsSessionActive(activeSession !== null);
                setScheduleInfo({
                    location: activeSession?.location_name || res.practice?.location_name || '-',
                    date: activeSession?.date || res.practice?.date || '-',
                    time: activeSession?.start_time || res.practice?.start_time || '-',
                    name: activeSession?.name || res.practice?.name || '-',
                    status: activeSession?.status || res.practice?.status || '-',
                    sessionDate: activeSession?.date || null,
                    practiceDate: res.practice?.date || null,
                });

                const sessionAttendances = activeSession !== null
                    ? activeSession.students.map((attendance) => ({
                          ...attendance,
                          status: attendance.status ? attendance.status.toLowerCase() : ATTENDANCE_STATUS.PENDING,
                      }))
                    : [];

                const activePractice = res.practice && res.practice.status !== "CANCELLED" ? res.practice : null;
                const practiceAttendances = activePractice !== null
                    ? (activePractice.students ?? []).map((attendance) => ({
                          ...attendance,
                          status: attendance.status ? attendance.status.toLowerCase() : ATTENDANCE_STATUS.PENDING,
                      }))
                    : [];

                setSessionAttendanceList(sessionAttendances || []);
                setPracticeAttendanceList(practiceAttendances || []);

                const pid = activePractice?.id ?? (type === "practice" && activePractice !== null ? Number(classId) : null);
                setPracticeId(pid);
                if (pid) {
                    getPracticeScheduleDetail(pid).then((detail) => {
                        setPracticeDetail(detail);
                    }).catch(() => {});
                }
            }
        });
    }, [classId, type]);


    const handleStartMessage = async (userId) => {

      try {
          const room = await startDirectMessageRoom(userId);
          if (room?.id) {
              navigate(`/messages/${encodeURIComponent(room.id)}`);
              return;
          }
          showError("메시지방을 시작하지 못했습니다.");
      } catch {
          showError("메시지방을 시작하지 못했습니다.");
      }
  };

    const handleStatusChange = useCallback((index, status) => {
        setSessionAttendanceList((prevList) => {
            const newList = [...prevList];
            newList[index] = { ...newList[index], status };
            return newList;
        });
    }, []);

    const handleSaveAllAttendance = () => {
        const lessonDate = scheduleInfo?.sessionDate || scheduleInfo?.date;
        if (lessonDate && dayjs(lessonDate).isAfter(dayjs(), "day")) {
            showError("수업이 종료되지 않았습니다.");
            return;
        }

        const allSelected = sessionAttendanceList.every(
            (student) => student.status === ATTENDANCE_STATUS.ATTENDED || student.status === ATTENDANCE_STATUS.NEEDS_RETAKE
        );

        if (!allSelected) {
            alert("모든 학생의 출결 상태를 선택해주세요.");
            return;
        }

        if (sessionAttendanceList.length === 0) {
            alert("저장할 학생이 없습니다.");
            return;
        }

        const attendanceData = sessionAttendanceList.map((student) => ({
            reservation_id: student.id,
            status: student.status.toUpperCase(),
        }));

        setIsSaving(true);
        api.post(`/buccl_lessons/api/v1/instructor-schedules/${classId}/complete/`, { attendance: attendanceData })
            .then(() => {
                showSuccess("출결 상태가 저장되었습니다.");
            })
            .catch((err) => {
                console.error("출결 저장 실패:", err);
                showError(err?.response?.data?.error || "출결 저장 중 오류가 발생했습니다.");
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const handlePracticeComplete = () => {
        if (isCompleting) return;
        const practiceDate = scheduleInfo?.practiceDate || scheduleInfo?.date;
        if (practiceDate && dayjs(practiceDate).isAfter(dayjs(), "day")) {
            showError("수업이 종료되지 않았습니다.");
            return;
        }
        const studentCount = practiceAttendanceList.length > 0 ? practiceAttendanceList.length : (practiceDetail?.student_count ?? 0);
        if (studentCount === 0) {
            showError("정산할 수강생이 없습니다.");
            return;
        }
        setIsCompleting(true);
        api.post(`/buccl_lessons/api/v1/practice-schedules/${practiceId}/complete/`)
            .then((res) => {
                showSuccess(res.data?.message || "연습반 정산이 완료되었습니다.");
                setPracticeDetail((prev) => ({ ...prev, status: "CLOSED" }));
            })
            .catch((err) => {
                showError(err?.response?.data?.error || "정산 처리 중 오류가 발생했습니다.");
            })
            .finally(() => {
                setIsCompleting(false);
            });
    };

    return (
        <>
            <SubHeader title="스케쥴 관리" />
            <div className="admin-attendance-detail-container">

                {scheduleInfo && (
                    <div className="schedule-hero-card">
                        <div className="hero-header">
                            <h2 className="hero-name">{scheduleInfo.name}</h2>
                        </div>
                        {scheduleInfo.status === "CANCELLED" && (
                            <div className="cancel-badge-modern">취소된 스케쥴입니다</div>
                        )}
                        <div className="hero-info-grid">
                            <div className="info-cell">
                                <div className="icon-box"><FontAwesomeIcon icon={faCalendar} /></div>
                                <div className="cell-content">
                                    <span className="label">Date</span>
                                    <span className="value">{dayjs(scheduleInfo.date).format('YY.MM.DD')} ({dayjs(scheduleInfo.date).format('ddd')})</span>
                                </div>
                            </div>
                            <div className="info-cell">
                                <div className="icon-box"><FontAwesomeIcon icon={faClock} /></div>
                                <div className="cell-content">
                                    <span className="label">Time</span>
                                    <span className="value">{scheduleInfo.time}</span>
                                </div>
                            </div>
                            <div className="info-cell full-width" style={{ marginTop: '4px' }}>
                                <div className="icon-box"><FontAwesomeIcon icon={faMapMarkerAlt} /></div>
                                <div className="cell-content">
                                    <span className="label">Location</span>
                                    <span className="value">{scheduleInfo.location}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {type !== "practice" && isSessionActive && (
                    <div className="admin-attendance-detail-page">
                        <div className="section-header">
                            <div className="indicator lesson"></div>
                            <h2>강습 수강생 명단</h2>
                            <span className="count">{sessionAttendanceList.length}명</span>
                        </div>
                        <div className="student-list">
                            {sessionAttendanceList.length === 0 ? (
                                <div className="empty-list-card">
                                    <p>예약한 수강생이 없습니다</p>
                                </div>
                            ) : (
                                sessionAttendanceList.map((attendance, index) => (
                                    <div className="student-card" key={attendance.id || index}>
                                        <div className="card-top">
                                            <div className="student-main">
                                                <div className="name-row">
                                                    <span className="name">{attendance.name}</span>
                                                    <span className="gender-badge">{attendance.gender === "M" ? "남성" : "여성"}</span>
                                                </div>
                                                <a href={`tel:${attendance.hp}`} className="phone-link">
                                                    <FontAwesomeIcon icon={faPhone} />
                                                    {attendance.hp || "-"}
                                                </a>
                                            </div>
                                            <div className="attendance-btn-wrap">
                                                <button 
                                                    className="btn-pb" 
                                                    onClick={() => setPbTarget({ userId: attendance.user_id, public_id: attendance.user?.public_id, name: attendance.name })}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                    PB 입력
                                                </button>
                                                <button 
                                                    className="btn-pb" 
                                                    onClick={() => handleStartMessage(attendance.user?.public_id)}
                                                >
                                                    <FontAwesomeIcon icon={faMessage} />
                                                    메세지
                                                </button>
                                            </div>

                                        </div>
                                        <div className="card-meta">
                                            <span className="meta-tag">{attendance.birthday ? dayjs(attendance.birthday).format('YY.MM.DD') : '생일 미등록'}</span>
                                            <span className="meta-tag">
                                                {!Array.isArray(attendance.certificates) || attendance.certificates.length === 0
                                                    ? "자격증 미등록"
                                                    : attendance.certificates.map(c => c.name).join(", ")}
                                            </span>
                                        </div>
                                        <div className="attendance-segmented">
                                            <label>
                                                <input 
                                                    type="radio" 
                                                    name={`status-${attendance.id || index}`}
                                                    checked={attendance.status === ATTENDANCE_STATUS.ATTENDED}
                                                    onChange={() => handleStatusChange(index, ATTENDANCE_STATUS.ATTENDED)}
                                                />
                                                <span className="toggle-btn type-attended">출석</span>
                                            </label>
                                            <label>
                                                <input 
                                                    type="radio" 
                                                    name={`status-${attendance.id || index}`}
                                                    checked={attendance.status === ATTENDANCE_STATUS.NEEDS_RETAKE}
                                                    onChange={() => handleStatusChange(index, ATTENDANCE_STATUS.NEEDS_RETAKE)}
                                                />
                                                <span className="toggle-btn type-absent">결석 / 재수강</span>
                                            </label>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="section-footer-btns">
                            <button
                                className="btn-inline"
                                onClick={() => {
                                    // 강습·연습반이 동시 개설된 경우, 수업 정보 수정이 연습반에 반영되지 않음을 안내한다.
                                    if (practiceId) {
                                        const proceed = window.confirm(
                                            "(중요!!) 수업정보를 변경해도 연습반 정보는 변경되지 않습니다. 연습반도 정보 수정이 필요합니다."
                                        );
                                        if (!proceed) return;
                                    }
                                    navigate(`/class/edit/${classId}`);
                                }}
                                disabled={scheduleInfo?.status === "CANCELLED"}
                            >
                                <FontAwesomeIcon icon={faEdit} />
                                수업 스케쥴 수정
                            </button>
                            <button
                                className="btn-inline primary"
                                onClick={handleSaveAllAttendance}
                                disabled={isSaving || scheduleInfo?.status === "CANCELLED"}
                            >
                                <FontAwesomeIcon icon={faCheckCircle} />
                                {isSaving ? "저장 중..." : "출결 저장"}
                            </button>
                        </div>
                    </div>
                )}

                {practiceId && (
                    <div className="admin-attendance-detail-page">
                        <div className="section-header">
                            <div className="indicator practice"></div>
                            <h2>연습반 명단</h2>
                            {practiceDetail?.pricing_type === "PAID" && <span className="premium-pill">Premium</span>}
                            {scheduleInfo?.sessionDate &&
                                scheduleInfo?.practiceDate &&
                                !dayjs(scheduleInfo.sessionDate).isSame(scheduleInfo.practiceDate, "day") && (
                                <span className="date-mismatch-pill" style={{ marginLeft: "8px", fontSize: "12px", fontWeight: 600, color: "#d32f2f", background: "#fdecea", borderRadius: "4px", padding: "2px 8px" }}>
                                    수업개설 날짜와 다릅니다
                                </span>
                            )}
                            <span className="count">{practiceAttendanceList.length}명</span>
                        </div>
                        <div className="practice-list">
                            {practiceAttendanceList.length === 0 ? (
                                <div className="empty-list-card">
                                    <p>예약한 수강생이 없습니다</p>
                                </div>
                            ) : (
                                practiceAttendanceList.map((attendance, index) => (
                                    <div className="student-card" key={attendance.id || index}>
                                        <div className="card-top">
                                            <div className="student-main">
                                                <div className="name-row">
                                                    <span className="name">{attendance.name}</span>
                                                    <span className="gender-badge">{attendance.gender === "M" ? "남성" : "여성"}</span>
                                                </div>
                                                <a href={`tel:${attendance.hp}`} className="phone-link">
                                                    <FontAwesomeIcon icon={faPhone} />
                                                    {attendance.hp || "-"}
                                                </a>
                                            </div>
                                            <div className="attendance-btn-wrap">
                                                <button
                                                    className="btn-pb"
                                                    onClick={() => setPbTarget({ userId: attendance.user_id, public_id: attendance.user?.public_id, name: attendance.name })}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                    PB 입력
                                                </button>
                                                <button 
                                                    className="btn-pb" 
                                                    onClick={() => handleStartMessage(attendance.user?.public_id)}
                                                >
                                                    <FontAwesomeIcon icon={faMessage} />
                                                    메세지
                                                </button>
                                            </div>
                                        </div>
                                        <div className="card-meta">
                                            <span className="meta-tag">{attendance.birthday ? dayjs(attendance.birthday).format('YY.MM.DD') : '생일 미등록'}</span>
                                            <span className="meta-tag">
                                                {!Array.isArray(attendance.certificates) || attendance.certificates.length === 0
                                                    ? "자격증 미등록"
                                                    : attendance.certificates.map(c => c.name).join(", ")}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="section-footer-btns">
                            <button
                                className="btn-inline"
                                onClick={() => navigate(`/practice/edit/${practiceId}`)}
                                disabled={scheduleInfo?.status === "CANCELLED"}
                            >
                                <FontAwesomeIcon icon={faEdit} />
                                연습반 수정
                            </button>
                            <button
                                className="btn-inline practice"
                                onClick={handlePracticeComplete}
                                disabled={isCompleting || practiceDetail?.status === "CLOSED" || scheduleInfo?.status === "CANCELLED"}
                            >
                                <FontAwesomeIcon icon={faCheckCircle} />
                                {practiceDetail?.status === "CLOSED" ? "정산 완료" : isCompleting ? "처리 중..." : "연습반 정산"}
                            </button>
                        </div>
                    </div>
                )}

            </div>

            <PbInputSheet
                isOpen={!!pbTarget}
                onClose={() => setPbTarget(null)}
                userId={pbTarget?.userId}
                historyUserKey={getPublicUserKey(pbTarget)}
                userName={pbTarget?.name}
            />
        </>
    );
};

export default AdminAttendanceDetail;
